/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, IMemberSearchFilters } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class AlternatePhoneNumbers extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable.boolean()
    public readonly: boolean = false;

    protected validateForm = validate;
    protected savePhoneNumberIsValid: boolean = false;

    protected canAddAlternatePhoneNumber: boolean = false;

    protected types: Core.PhoneNumberType[] = [];
    protected numberOfTypes: number = 0;

    protected editedPhoneNumber: CD.MemberPhoneNumber | null = null;

    protected sorting = new Sorting();
    protected alternatePhoneNumbers: CD.MemberPhoneNumber[] = [];


    ////****************** Element References ***************************************////
    private $savePhoneNumberModal!: IPopup;
    protected $savePhoneNumberForm!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'type.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberPhoneNumbers();
        await this.loadLookups();
        this.canAddAlternatePhoneNumber = this.alternatePhoneNumbers.length < this.numberOfTypes;
    }


    ////****************** Functions ************************************************////
    protected removeExtension(number: string): string {
        const index = number.indexOf('x');
        if(index > -1) {
            return number.slice(0, index);
        }
        return number;
    }

    protected closeSavePhoneNumberModal(): void {
        if(this.$savePhoneNumberForm.reset) {
            this.$savePhoneNumberForm.reset();
        }
        this.$savePhoneNumberModal.close();
    }

    protected async handleSavePhoneNumberFormSuccess(): Promise<void> {
        if(this.$savePhoneNumberForm.reset) {
            this.$savePhoneNumberForm.reset();
        }
        this.$savePhoneNumberModal.close();
        await this.reload();
    }

    protected savePhoneNumber(phoneNumber: CD.MemberPhoneNumber | null = null): void {
        this.editedPhoneNumber = phoneNumber ?? {};
        this.$savePhoneNumberModal.open();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberPhoneNumbers(): Promise<void> {
        if(!this.memberId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            includeInactive: true
        };

        const includes: string[] = [
            'type',
        ];

        const searchFilters: IMemberSearchFilters = {
            alternateTypes: true
        }

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberPhoneNumbers(this.memberId, undefined, options, includes, [], searchFilters);
            this.alternatePhoneNumbers = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const searchFilters: IMemberSearchFilters = {
                alternateTypes: true
            }

            const types = await this.memberService.loadMemberPhoneNumberTypes({}, [], searchFilters);

            this.types = types.results;
            this.numberOfTypes = this.types.length;
            // Remove types already in use
            this.alternatePhoneNumbers.forEach(phoneNumber => {
                const indexToRemove = this.types.findIndex(type => type?.id === phoneNumber?.type?.id)
                this.types.splice(indexToRemove, 1);
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activatePhoneNumber(phoneNumber: CD.MemberPhoneNumber): Promise<void> {
        if(!phoneNumber.id) return;

        // Show confirmation dialog only if deactivating
        if(phoneNumber.isActive) {
            const result = await confirmation.showDeactivate('cd:memberContact.phoneNumber.alternate.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberPhoneNumber(phoneNumber.id, !phoneNumber.isActive);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.activate('cd:memberContact.phoneNumber.alternate.$', false, false);
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:memberContact.phoneNumber.alternate.$', !phoneNumber.isActive, isSuccessful);
            await this.reload();
            this.state.endSave();
        }
    }
}
